/**
 * Formulaire de contact avancé (blocks/contact-form-plus.liquid).
 *
 * - Affiche la recherche de produits / le n° de commande selon le sujet choisi.
 * - Les produits ajoutés partent dans l'email sous la forme « Produit 1 : Titre - URL ».
 * - Photos : le formulaire Shopify n'accepte pas de fichier, un bouton ouvre donc la messagerie
 *   du client (mailto) avec l'objet, le n° de commande et les produits déjà remplis.
 * - Anti-spam : champ piège + champ « Anti-spam » (OK / SUSPECT - raison) pour filtrer dans Gmail.
 *
 * Paramètres d'URL acceptés : ?sujet=<id du bloc sujet, ex. casse>&produit=<handle>&variante=<id variante>
 * (lien « J'ai une question » des fiches produits : blocks/product-question.liquid).
 */

const MIN_DELAY_MS = 3000;
const MAX_PRODUCTS = 10;
const MAX_RESULTS = 60;
const MAX_PAGES = 8;

// Démarchage : comparés au texte sans accents ni majuscules (FR / EN / HU).
const SPAM_PATTERNS = [
  'seo',
  'backlinks?',
  'guest ?posts?',
  'link ?building',
  'referencement',
  'keresooptimalizalas',
  'domain authority',
  'first page (of|on) google',
  'premiere page (de|sur) google',
  'google (ranking|rankings)',
  'web ?design(er)?',
  'website redesign',
  'refonte (de|du) (votre )?site',
  'website development',
  'developpement web',
  'app development',
  'digital marketing',
  'marketing digital',
  'marketing agency',
  'agence (web|marketing|seo|digitale)',
  'marketing ugynokseg',
  'lead generation',
  '(more|qualified) leads',
  'increase (your )?(sales|traffic|revenue)',
  'boost (your )?(sales|traffic|revenue)',
  'augmenter (vos|votre) (ventes|chiffre d.affaires|trafic)',
  'influencers?',
  'influenceu(r|se)s?',
  'ugc',
  'sponsored',
  'sponsoris(e|ee|es)',
  'collaboration',
  'partnership',
  'partenariat',
  'egyuttmukodes',
  'dropshipping',
  'crypto',
  'bitcoin',
  'forex',
  'casino',
  'viagra',
  'shopify (expert|developer|store owner)',
  'conversion rate',
  'taux de conversion',
  '(facebook|google|tiktok|meta|instagram) ads',
  'virtual assistant',
  'outreach',
  'unsubscribe',
  'desabonner',
  'desinscri(re|ption)',
];
const SPAM_REGEX = SPAM_PATTERNS.map((pattern) => new RegExp(`(^|[^a-z0-9])(${pattern})(?=$|[^a-z0-9])`));
const FOREIGN_SCRIPT = /[\u0400-\u04ff\u0590-\u05ff\u0600-\u06ff\u0900-\u097f\u0e00-\u0e7f\u3040-\u30ff\u4e00-\u9fff\uac00-\ud7af]/;
const URL_REGEX = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

const normalize = (text) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

class ContactFormPlus extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((element) => {
      this.refs[element.dataset.ref] = element;
    });
    // Page de confirmation après envoi : il n'y a plus de champs.
    if (!this.refs.topic) return;

    this.startedAt = Date.now();
    this.interacted = false;
    this.products = null;
    this.picked = [];
    this.results = [];
    this.activeIndex = -1;
    this.suggested = null;

    ['keydown', 'pointerdown', 'touchstart', 'input', 'paste'].forEach((type) => {
      this.form.addEventListener(type, this.onInteraction, { passive: true });
    });
    this.form.addEventListener('input', this.onInput);
    this.refs.topic.addEventListener('change', this.onTopicChange);
    this.refs.search.addEventListener('focus', this.onSearchFocus);
    this.refs.search.addEventListener('input', this.onSearchInput);
    this.refs.search.addEventListener('keydown', this.onSearchKeydown);
    this.refs.search.addEventListener('blur', this.onSearchBlur);
    this.refs.list.addEventListener('mousedown', (event) => event.preventDefault());
    this.refs.list.addEventListener('click', this.onListClick);
    this.refs.picked.addEventListener('click', this.onPickedClick);
    this.refs.suggest.addEventListener('click', this.onSuggestClick);
    // Phase de capture : passe avant les autres écouteurs du formulaire (dont hCaptcha).
    document.addEventListener('submit', this.onSubmit, true);
    window.addEventListener('pageshow', this.onPageShow);

    this.enhanceTopicSelect();
    this.applyUrlParams();
    this.onTopicChange();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmit, true);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onInteraction = () => {
    this.interacted = true;
  };

  onInput = (event) => {
    if (event.target === this.refs.search) return;
    this.updateSpamFlag();
    this.updatePhotosLink();
  };

  onPageShow = () => {
    // Retour arrière (page de vérification hCaptcha) : réactive les champs coupés à l'envoi.
    this.refs.honeypot.disabled = false;
    this.refs.topic.disabled = false;
  };

  /* ---------- Sujet ---------- */

  onTopicChange = () => {
    this.syncTopicButton();
    const option = this.refs.topic.selectedOptions[0];
    const showProduct = option?.dataset.product === 'true';
    const showOrder = option?.dataset.order === 'true';
    const showPhotos = option?.dataset.photos === 'true';
    const hint = option?.dataset.hint || '';

    this.toggleGroup(this.refs['product-group'], showProduct);
    this.toggleGroup(this.refs['order-group'], showOrder);
    this.refs['photos-group'].hidden = !showPhotos;
    this.updatePhotosLink();
    this.refs.hint.innerHTML = hint;
    this.refs.hint.hidden = !hint;

    if (showProduct) this.loadProducts();
    this.updateSpamFlag();
  };

  toggleGroup(group, visible) {
    group.hidden = !visible;
    // Un champ masqué est désactivé pour ne pas apparaître (vide) dans l'email.
    group.querySelectorAll('input[name]').forEach((input) => {
      input.disabled = !visible;
    });
  }

  applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const options = [...this.refs.topic.options];
    const topicKey = params.get('sujet');
    // L'id des blocs sujets est généré par Shopify (« …__casse ») : on compare le suffixe.
    const topic =
      topicKey &&
      options.find((option) => option.dataset.key === topicKey || option.dataset.key?.endsWith(`__${topicKey}`));
    if (topic) topic.selected = true;

    const handle = params.get('produit') || this.referrerHandle();
    if (!handle) return;

    const variantId = params.get('produit') ? params.get('variante') : null;
    this.loadProducts().then(async (products) => {
      let product = products.find((item) => item.h === handle);
      // Variante demandée, ou produit absent du catalogue : on lit la fiche /products/<handle>.js.
      if (params.get('produit') && (variantId || !product)) {
        const data = await this.fetchProduct(handle);
        if (data) {
          if (!product) {
            const image = data.featured_image || '';
            product = { h: data.handle, t: data.title, i: image && `${image}${image.includes('?') ? '&' : '?'}width=80` };
          }
          const variant = data.variants?.find((item) => String(item.id) === String(variantId));
          if (variant && data.variants.length > 1) product = { ...product, v: variant.title, vid: variant.id };
        }
      }
      if (!product) return;

      if (params.get('produit')) {
        if (this.refs.topic.selectedOptions[0]?.dataset.product !== 'true') {
          const productTopic = options.find((option) => option.dataset.product === 'true');
          if (productTopic) productTopic.selected = true;
          this.onTopicChange();
        }
        this.addProduct(product);
        this.focusAfterPrefill();
      } else {
        this.showSuggestion(product);
      }
    });
  }

  fetchProduct(handle) {
    return fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  // Sujet et produit déjà remplis : amène le client au premier champ encore vide (nom, email, message).
  focusAfterPrefill() {
    const fields = ['contact[first_name]', 'contact[last_name]', 'contact[email]', 'contact[body]']
      .map((name) => this.form.elements[name])
      .filter(Boolean);
    const target = fields.find((field) => !field.value.trim()) || this.form.elements['contact[body]'];
    if (!target) return;
    this.refs['product-group'].scrollIntoView({ block: 'start', behavior: 'smooth' });
    try {
      target.focus({ preventScroll: true });
    } catch {
      /* navigateur ancien */
    }
  }

  productLabel(product) {
    return product.v ? `${product.t} (${product.v})` : product.t;
  }

  productUrl(product) {
    return `${window.location.origin}/products/${product.h}${product.vid ? `?variant=${product.vid}` : ''}`;
  }

  referrerHandle() {
    try {
      const url = new URL(document.referrer);
      if (url.host !== window.location.host) return null;
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  /* ---------- Liste des sujets (ordinateur) ---------- */

  // La liste native de Chrome / Edge sous Windows surligne l'option sur une demi-largeur : sur ordinateur,
  // le <select> (conservé pour l'envoi) est masqué et remplacé par une liste dessinée par le site.
  enhanceTopicSelect() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const select = this.refs.topic;
    const button = document.createElement('button');
    const list = document.createElement('ul');

    button.type = 'button';
    button.id = `${select.id}-button`;
    button.className = 'select select--full-width contact-form-plus__topic-button';
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', `${select.id}-list`);
    button.append(document.createElement('span'));

    list.id = `${select.id}-list`;
    list.className = 'contact-form-plus__list contact-form-plus__topic-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    list.append(
      ...[...select.options].map((option, index) => {
        const item = document.createElement('li');
        item.id = `${list.id}-${index}`;
        item.className = 'contact-form-plus__option';
        item.dataset.index = index;
        item.setAttribute('role', 'option');
        item.textContent = option.textContent.trim();
        return item;
      })
    );

    select.hidden = true;
    select.tabIndex = -1;
    select.parentElement.prepend(button);
    select.parentElement.append(list);
    const label = this.querySelector(`label[for="${select.id}"]`);
    if (label) label.htmlFor = button.id;

    this.topicButton = button;
    this.topicList = list;
    this.topicActive = select.selectedIndex;

    button.addEventListener('click', () => (list.hidden ? this.openTopicList() : this.closeTopicList()));
    button.addEventListener('keydown', this.onTopicKeydown);
    button.addEventListener('blur', () => {
      setTimeout(() => {
        if (document.activeElement !== button) this.closeTopicList();
      }, 150);
    });
    list.addEventListener('mousedown', (event) => event.preventDefault());
    list.addEventListener('click', (event) => {
      const item = event.target.closest('[data-index]');
      if (item) this.chooseTopic(Number(item.dataset.index));
    });
    list.addEventListener('mousemove', (event) => {
      const item = event.target.closest('[data-index]');
      if (item && Number(item.dataset.index) !== this.topicActive) this.setTopicActive(Number(item.dataset.index));
    });
  }

  onTopicKeydown = (event) => {
    const count = this.topicList.children.length;

    if (this.topicList.hidden) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.openTopicList();
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.setTopicActive(Math.min(count - 1, Math.max(0, this.topicActive + step)));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.setTopicActive(event.key === 'Home' ? 0 : count - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.chooseTopic(this.topicActive);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeTopicList();
    } else if (event.key === 'Tab') {
      this.closeTopicList();
    }
  };

  openTopicList() {
    this.topicList.hidden = false;
    this.topicButton.setAttribute('aria-expanded', 'true');
    this.setTopicActive(this.refs.topic.selectedIndex);
  }

  closeTopicList() {
    if (!this.topicList || this.topicList.hidden) return;
    this.topicList.hidden = true;
    this.topicButton.setAttribute('aria-expanded', 'false');
    this.topicButton.removeAttribute('aria-activedescendant');
  }

  setTopicActive(index) {
    this.topicActive = index;
    [...this.topicList.children].forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
      if (itemIndex === index) {
        this.topicButton.setAttribute('aria-activedescendant', item.id);
        item.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  chooseTopic(index) {
    const select = this.refs.topic;
    this.closeTopicList();
    if (select.selectedIndex !== index) {
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.topicButton.focus();
  }

  syncTopicButton() {
    if (!this.topicButton) return;
    const select = this.refs.topic;
    this.topicButton.firstElementChild.textContent = select.selectedOptions[0]?.textContent.trim() || '';
    [...this.topicList.children].forEach((item, index) => {
      item.setAttribute('aria-selected', String(index === select.selectedIndex));
    });
  }

  /* ---------- Catalogue ---------- */

  loadProducts() {
    if (!this.productsPromise) {
      const fetchPage = (page) =>
        fetch(`${this.dataset.productsUrl}&page=${page}`, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        });

      this.productsPromise = fetchPage(1)
        .then(async (first) => {
          const pages = Math.min(first.pages || 1, MAX_PAGES);
          const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, index) => fetchPage(index + 2)));
          const products = [first, ...rest].flatMap((page) => page.products);
          products.forEach((product) => {
            product.search = normalize(`${product.t} ${product.y || ''} ${product.h.replace(/-/g, ' ')}`);
          });
          return products;
        })
        .catch((error) => {
          console.error('contact-form-plus: catalogue indisponible', error);
          this.productsPromise = null;
          return [];
        });
    }
    return this.productsPromise;
  }

  /* ---------- Liste déroulante ---------- */

  openList = async () => {
    this.refs.list.hidden = false;
    this.refs.search.setAttribute('aria-expanded', 'true');
    if (!this.products) {
      this.renderMessage(this.dataset.i18nLoading);
      const products = await this.loadProducts();
      if (products.length) this.products = products;
    }
    if (!this.refs.list.hidden) this.renderResults();
  };

  onSearchFocus = () => {
    this.openList();
    // Mobile : remonte la recherche en haut de l'écran, sinon le clavier cache la liste.
    if (window.matchMedia('(max-width: 749px)').matches) {
      setTimeout(() => this.refs.search.parentElement.scrollIntoView({ block: 'start', behavior: 'smooth' }), 300);
    }
  };

  closeList() {
    this.refs.list.hidden = true;
    this.refs.search.setAttribute('aria-expanded', 'false');
    this.refs.search.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  onSearchInput = () => {
    if (this.refs.list.hidden || !this.products) {
      this.openList();
    } else {
      this.renderResults();
    }
  };

  onSearchBlur = () => {
    setTimeout(() => {
      if (document.activeElement !== this.refs.search) this.closeList();
    }, 150);
  };

  onSearchKeydown = (event) => {
    if (event.key === 'Enter') {
      // Entrée dans la recherche ajoute le produit au lieu d'envoyer le formulaire.
      event.preventDefault();
      if (this.refs.list.hidden) return;
      if (this.activeIndex >= 0) this.selectResult(this.activeIndex);
      else if (this.results.length === 1) this.selectResult(0);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.refs.list.hidden) {
        this.openList();
        return;
      }
      if (!this.results.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.activeIndex + step + this.results.length) % this.results.length);
    } else if (event.key === 'Escape' && !this.refs.list.hidden) {
      event.preventDefault();
      this.closeList();
    }
  };

  onListClick = (event) => {
    const option = event.target.closest('[data-index]');
    if (option) this.selectResult(Number(option.dataset.index));
  };

  setActive(index) {
    this.activeIndex = index;
    this.refs.list.querySelectorAll('[role="option"]').forEach((option, optionIndex) => {
      option.setAttribute('aria-selected', String(optionIndex === index));
      if (optionIndex === index) {
        this.refs.search.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  renderResults() {
    if (!this.products) return;

    const words = normalize(this.refs.search.value.trim()).split(/\s+/).filter(Boolean);
    const matches = words.length
      ? this.products.filter((product) => words.every((word) => product.search.includes(word)))
      : this.products;
    this.results = matches.slice(0, MAX_RESULTS);
    this.activeIndex = -1;
    this.refs.search.removeAttribute('aria-activedescendant');

    if (!this.results.length) {
      this.renderMessage(this.dataset.i18nNoResult);
      return;
    }

    const items = this.results.map((product, index) => {
      const item = document.createElement('li');
      const picked = this.isPicked(product);
      item.className = 'contact-form-plus__option';
      item.id = `${this.refs.list.id}-${index}`;
      item.dataset.index = index;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      if (picked) item.setAttribute('aria-disabled', 'true');
      item.append(this.thumb(product), Object.assign(document.createElement('span'), { textContent: product.t }));
      if (picked) item.append(this.icon('check'));
      return item;
    });

    if (matches.length > MAX_RESULTS) {
      const more = document.createElement('li');
      more.className = 'contact-form-plus__empty';
      more.setAttribute('role', 'presentation');
      more.textContent = this.dataset.i18nMore;
      items.push(more);
    }

    this.refs.list.replaceChildren(...items);
    this.refs.list.scrollTop = 0;
  }

  renderMessage(text) {
    const item = document.createElement('li');
    item.className = 'contact-form-plus__empty';
    item.setAttribute('role', 'presentation');
    item.textContent = text;
    this.refs.list.replaceChildren(item);
  }

  selectResult(index) {
    const product = this.results[index];
    if (!product || this.isPicked(product)) return;
    this.addProduct(product);
    this.refs.search.value = '';
    this.closeList();
  }

  /* ---------- Produits ajoutés ---------- */

  isPicked(product) {
    return this.picked.some((item) => item.h === product.h);
  }

  addProduct(product) {
    if (this.isPicked(product) || this.picked.length >= MAX_PRODUCTS) return;
    this.picked.push(product);
    if (this.suggested?.h === product.h) this.hideSuggestion();
    this.renderPicked();
  }

  onPickedClick = (event) => {
    const button = event.target.closest('[data-handle]');
    if (!button) return;
    this.picked = this.picked.filter((item) => item.h !== button.dataset.handle);
    this.renderPicked();
    this.refs.search.focus();
  };

  renderPicked() {
    const groupVisible = !this.refs['product-group'].hidden;

    this.refs.picked.replaceChildren(...this.picked.map((product) => this.chip(product)));
    this.refs['product-inputs'].replaceChildren(
      ...this.picked.map((product, index) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[Produit ${index + 1}]`;
        input.value = `${this.productLabel(product)} - ${this.productUrl(product)}`;
        input.disabled = !groupVisible;
        return input;
      })
    );

    this.refs.search.placeholder = this.picked.length
      ? this.dataset.i18nAddAnother
      : this.refs.search.dataset.placeholder;
    this.refs.search.closest('.contact-form-plus__combo').hidden = this.picked.length >= MAX_PRODUCTS;
    this.updatePhotosLink();
  }

  chip(product) {
    const chip = document.createElement('div');
    chip.className = 'contact-form-plus__chip';

    const title = document.createElement('span');
    title.className = 'contact-form-plus__chip-title';
    title.textContent = this.productLabel(product);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'contact-form-plus__chip-remove';
    remove.dataset.handle = product.h;
    remove.setAttribute('aria-label', `${this.dataset.i18nRemove} : ${product.t}`);
    remove.append(this.icon('close'));

    chip.append(this.thumb(product), title, remove);
    return chip;
  }

  showSuggestion(product) {
    if (this.isPicked(product)) return;
    this.suggested = product;

    const label = document.createElement('span');
    label.className = 'contact-form-plus__chip-title';
    label.append(
      Object.assign(document.createElement('small'), { textContent: this.dataset.i18nSuggest }),
      document.createTextNode(product.t)
    );

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'contact-form-plus__suggest-add';
    add.textContent = this.dataset.i18nAdd;

    this.refs.suggest.replaceChildren(this.thumb(product), label, add);
    this.refs.suggest.hidden = false;
  }

  hideSuggestion() {
    this.suggested = null;
    this.refs.suggest.hidden = true;
  }

  onSuggestClick = (event) => {
    if (!event.target.closest('button') || !this.suggested) return;
    this.addProduct(this.suggested);
  };

  thumb(product) {
    const image = document.createElement(product.i ? 'img' : 'span');
    image.className = 'contact-form-plus__thumb';
    if (product.i) {
      Object.assign(image, { src: product.i, alt: '', width: 40, height: 40, loading: 'lazy' });
    }
    return image;
  }

  icon(name) {
    const icon = this.refs.search.parentElement.querySelector('.icon').cloneNode();
    icon.classList.replace('icon--search', `icon--${name}`);
    icon.textContent = name;
    // Thèmes à icônes SVG (<theme-icon data-icon-name="search">) : l'image suit data-icon-name.
    if (icon.dataset.iconName) {
      icon.dataset.iconName = name;
      icon.style.removeProperty('--icon-url');
    }
    return icon;
  }

  /* ---------- Photos par email ---------- */

  updatePhotosLink() {
    const link = this.refs['photos-link'];
    if (!link || this.refs['photos-group'].hidden) return;

    const { email, subject, greeting, body, colon, topicLabel, orderLabel, productsLabel, messageLabel } = link.dataset;
    const value = (name) => this.form.elements[name]?.value.trim() || '';
    // Le sujet est repris tel que le client le voit (dans sa langue).
    const topic = this.refs.topic.value ? this.refs.topic.selectedOptions[0].textContent.trim() : '';
    const order = this.refs.order.disabled ? '' : this.refs.order.value.trim();
    const products = this.refs['product-group'].hidden ? [] : this.picked;
    const message = value('contact[body]');
    const name = [value('contact[first_name]'), value('contact[last_name]')].filter(Boolean).join(' ');

    // Objet : « [sujet] – Commande [commande] – Photos », sans les parties dont la valeur est vide.
    const tokens = { '[sujet]': topic, '[commande]': order };
    const fullSubject = subject
      .split(' – ')
      .filter((part) => Object.entries(tokens).every(([token, text]) => !part.includes(token) || text))
      .map((part) => Object.entries(tokens).reduce((text, [token, replacement]) => text.replace(token, replacement), part))
      .join(' – ');

    // Corps : récapitulatif de la demande, puis la phrase sur les photos et le nom du client.
    const productUrl = (product) => `${this.productLabel(product)} (${this.productUrl(product)})`;
    const lines = [greeting, ''];
    if (topic) lines.push(`${topicLabel}${colon}${topic}`);
    if (order) lines.push(`${orderLabel}${colon}${order}`);
    if (products.length === 1) lines.push(`${productsLabel}${colon}${productUrl(products[0])}`);
    if (products.length > 1) lines.push(`${productsLabel}${colon}`, ...products.map((product) => `- ${productUrl(product)}`));
    // Les messageries tronquent les liens mailto trop longs : le message est limité à 800 caractères.
    if (message) lines.push('', `${messageLabel}${colon.trimEnd()}`, message.slice(0, 800));
    lines.push('', body);
    if (name) lines.push('', name);

    const fullBody = `${lines.join('\n')}\n\n`;
    link.href = `mailto:${email}?subject=${encodeURIComponent(fullSubject)}&body=${encodeURIComponent(fullBody)}`;
  }

  /* ---------- Anti-spam ---------- */

  updateSpamFlag(submitting = false) {
    // Sans interaction réelle, la valeur par défaut (SUSPECT - envoi automatique) reste en place.
    if (!this.interacted) return;

    const option = this.refs.topic.selectedOptions[0];
    const order = this.refs.order;
    const text = ['contact[first_name]', 'contact[last_name]', 'contact[body]']
      .map((name) => this.form.elements[name]?.value || '')
      .join(' ');
    const reasons = [];

    if (option?.dataset.commercial === 'true') reasons.push('sujet démarchage');

    // Un vrai n° de commande l'emporte sur les mots-clés et les liens.
    const isCustomer = !order.disabled && /\d{3,}/.test(order.value);
    if (!isCustomer) {
      const normalized = normalize(text);
      const keywords = [
        ...new Set(SPAM_REGEX.map((regex) => normalized.match(regex)?.[2]).filter(Boolean)),
      ];
      if (keywords.length) reasons.push(`mots-clés : ${keywords.slice(0, 5).join(', ')}`);
      if (this.hasExternalLink(text)) reasons.push('lien externe');
      if (FOREIGN_SCRIPT.test(text)) reasons.push('alphabet étranger');
    }

    if (submitting && Date.now() - this.startedAt < MIN_DELAY_MS) reasons.push('envoyé en moins de 3 s');

    this.refs['spam-flag'].value = reasons.length ? `SUSPECT - ${reasons.join(' ; ')}` : 'OK';
  }

  hasExternalLink(text) {
    const host = window.location.hostname.replace(/^www\./, '');
    return (text.match(URL_REGEX) || []).some((raw) => {
      try {
        const url = new URL(raw.startsWith('www.') ? `https://${raw}` : raw);
        return !url.hostname.endsWith(host);
      } catch {
        return true;
      }
    });
  }

  onSubmit = (event) => {
    if (event.target !== this.form) return;

    // Robot : champ piège rempli ou aucune interaction réelle → rien n'est envoyé.
    if (this.refs.honeypot.value.trim() || !this.interacted) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.form.reset();
      this.refs.success.hidden = false;
      return;
    }

    this.updateSpamFlag(true);
    this.refs.honeypot.disabled = true;
    if (!this.refs.topic.value) this.refs.topic.disabled = true;
  };
}

if (!customElements.get('contact-form-plus')) {
  customElements.define('contact-form-plus', ContactFormPlus);
}
